import React, { useState } from 'react';
import { ModalWrapper } from './ModalWrapper';
import { POPUP_STYLES } from '../../constants/popupStyles';
import { meetingService } from '../../services/meetingService';

interface NoShowModalProps {
  isOpen: boolean;
  onClose: () => void;
  meetingId: number;
  onSuccess: () => void;
}

const NO_SHOW_REASONS = [
  'Technical issue (connection / audio / video)',
  'Patient unavailable or did not respond',
  'Pre-appointment requirements not completed',
  'Unsuitable environment',
  'Parents not present (pediatric only)',
  'Other',
];

export const NoShowModal: React.FC<NoShowModalProps> = ({
  isOpen,
  onClose,
  meetingId,
  onSuccess,
}) => {
  const [selectedReason, setSelectedReason] = useState('');
  const [additionalDetails, setAdditionalDetails] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleConfirm = async () => {
    if (!selectedReason) {
      setError('Please select a reason');
      return;
    }
    if (selectedReason === 'Other' && !additionalDetails.trim()) {
      setError('Please provide additional details');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      await meetingService.updateMeeting(meetingId, {
        status: 'noshow',
        reason_of_cancelation: selectedReason,
        cancellation_note: additionalDetails.trim() || undefined,
      });
      setSelectedReason('');
      setAdditionalDetails('');
      onSuccess();
      onClose();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to mark meeting as no-show');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setSelectedReason('');
      setAdditionalDetails('');
      setError(null);
      onClose();
    }
  };

  return (
    <ModalWrapper
      isOpen={isOpen}
      onClose={handleClose}
      title="No Show"
      isLoading={isSubmitting}
    >
      <div className="space-y-4">
        <div>
          <label className="block text-gray-700 font-medium mb-2">Reason *</label>
          <select
            value={selectedReason}
            onChange={(e) => setSelectedReason(e.target.value)}
            disabled={isSubmitting}
            className={`${POPUP_STYLES.input} appearance-none`}
          >
            <option value="">-- Select a reason --</option>
            {NO_SHOW_REASONS.map((reason) => (
              <option key={reason} value={reason}>{reason}</option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-gray-700 font-medium mb-2">Additional Details</label>
          <textarea
            value={additionalDetails}
            onChange={(e) => setAdditionalDetails(e.target.value)}
            disabled={isSubmitting}
            placeholder={selectedReason === 'Other' ? 'Please provide details...' : 'Optional additional information...'}
            rows={4}
            className={`${POPUP_STYLES.input} resize-none`}
          />
        </div>

        {error && (
          <div className="px-4 py-2 bg-red-100 border border-red-400 text-red-700 rounded">
            {error}
          </div>
        )}

        <div className="flex gap-4 justify-center pt-2">
          <button
            onClick={handleConfirm}
            disabled={!selectedReason || isSubmitting}
            className={POPUP_STYLES.button}
          >
            {isSubmitting ? 'Saving...' : 'Confirm'}
          </button>
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            className={POPUP_STYLES.button}
          >
            Cancel
          </button>
        </div>
      </div>
    </ModalWrapper>
  );
};
